import { withClass } from 'common/emotion'

import { calcRem } from 'component-library/__styling/style-functions'
import { breakWords } from 'component-library/__styling/style-mixins'
import { colors, metrics } from 'component-library/__styling/settings'

import { StyledLink as CardTextInteractions } from 'component-library/cards/CardText/CardTextInteractions.styled'
import { CardMedia } from 'component-library/cards/CardMedia/CardMedia'
import { CardHeadline } from 'component-library/cards/CardText/CardHeadline'
import { styledHeadlineTextClassName } from 'component-library/cards/CardText/CardText.styled'
import {
    SponsoredLabel,
    Props as SponsoredLabelProps,
} from 'component-library/advertising/SponsoredLabel/SponsoredLabel'
import { CardTeaser } from 'component-library/cards/CardText/CardTeaser'
import { themedValue } from 'component-library/__styling/themed-value'
import { Timestamp } from 'component-library/typography/Timestamp/Timestamp'
import { playButtonIconClass } from 'component-library/buttons/PlayButton/PlayButton.styled'
import { CardHeadlineSkeleton } from '../CardText/CardHeadlineSkeleton'
import styled from '@emotion/styled'

const styledLandscapeClassName = 'LandscapeStackedCard'
export const StyledLandscapeStacked = withClass(styledLandscapeClassName)(
    styled('div')(
        {
            ...breakWords,
            position: 'relative',
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            flexGrow: 1,
            background: colors.white,
        },
        ({ theme }) => ({
            borderBottom: themedValue(theme, {
                perthnow: `1px solid ${colors.perthnow.greyMarble}`,
                sevennews: `1px solid ${colors.sevennews.alto}`,
                thewest: undefined,
            }),
        })
    )
)

// Adds in the width and alignment styles for the wrapping link - hovers, focus, link styles come from CardTextInteractions component
export const StyledLink = styled(CardTextInteractions)(
    {
        display: 'flex',
        flex: '1 3 auto',
        flexWrap: 'wrap',
        alignItems: 'stretch',
    },
    ({ theme }) => ({
        padding: themedValue(theme, {
            thewest: calcRem(metrics.thewest.margins.sm),
            perthnow: calcRem(metrics.perthnow.margins.sm),
            sevennews: 0,
        }),

        [`& .${playButtonIconClass}`]: {
            opacity: 0,
        },

        [`&:hover .${playButtonIconClass}, &:focus .${playButtonIconClass}`]: {
            opacity: 1,
        },
    })
)

export const StyledHeader = styled('div')({
    color: colors.thewest.greyGorilla,
    fontSize: calcRem(16),
    lineHeight: 1.375,
    display: 'flex',
    flexWrap: 'wrap',
    flex: `1 1 ${calcRem(100)}`,
})

export const StyledHeadlineSkeleton = styled(CardHeadlineSkeleton)(({ theme }) => ({
    alignSelf: 'flex-start',

    [`& .${styledHeadlineTextClassName}`]: {
        transform: themedValue(theme, {
            perthnow: `translateY(-${calcRem(metrics.perthnow.cards.textOffset)})`,
            fallback: undefined,
        }),
    },
}))
// CardHeadline component alignment and fontsize overrides
export const StyledHeadline = StyledHeadlineSkeleton.withComponent(CardHeadline)

// Wrapper component for card text includes teaser and sponsored label
export const StyledText = styled('div')<{ sponsoredStyles: boolean }>(
    ({ theme, sponsoredStyles }) => ({
        display: 'flex',
        flexWrap: 'wrap',
        width: '100%',
        minHeight: themedValue(theme, {
            perthnow: sponsoredStyles ? undefined : calcRem(80),
            fallback: undefined,
        }),
    })
)
StyledText.displayName = 'StyledText'

// CardTeaser component font size overrides
export const StyledTeaser = styled(CardTeaser)({
    fontSize: calcRem(14),
})

// SponsoredLabel component overrides for margins and wrapping
export const StyledSponsoredLabel = styled(SponsoredLabel)<SponsoredLabelProps>(
    {
        flex: 'auto',
        alignSelf: 'flex-end',
    },
    ({ theme }) => ({
        textAlign: undefined,
        marginTop: themedValue(theme, { fallback: theme.margins.sm, perthnow: calcRem(18) }),
    })
)

// CardMedia component width & margin overrides
export const StyledMedia = styled(CardMedia)(({ theme }) => ({
    flex: `1 3 ${calcRem(106)}`,
    marginRight: theme.margins.sm,
    marginBottom: themedValue(theme, {
        thewest: calcRem(metrics.thewest.margins.sm),
        perthnow: calcRem(metrics.perthnow.cards.spacing),
        sevennews: calcRem(metrics.sevennews.margins.sm),
    }),
    maxWidth: themedValue(theme, {
        thewest: calcRem(106),
        perthnow: calcRem(106),
        sevennews: calcRem(126),
    }),
}))

// SevenNews Only
export const StyledTimestamp = styled(Timestamp)({
    marginBottom: metrics.sevennews.margins.sm + 2,
    alignSelf: 'flex-end',
})
